/**
 * Basemap panel — spec §F5.10 group 1: "basemap (panel with 4 thumb rows, active tinted)".
 *
 * Markup and geometry are the mockup's `#basemapPanel` / `.bm-opt` / `.bm-thumb`; the four
 * rows come from `basemaps.ts` (§F5.1's table, shared with `MapView`, which swaps the tile
 * layer when `basemap` changes). Rows are a radio group rather than the mockup's `<div>`s
 * — one basemap is on at a time, and ↑/↓ has to move between them (§I5).
 */
import { useTranslation } from 'react-i18next';

import { useAppStore, type Basemap } from '../../lib/state';
import { BASEMAPS, BASEMAP_KEYS } from './basemaps';

export interface BasemapPanelProps {
  /** Close after a pick, and on Esc — the panel is a popover off a toggle button. */
  onClose: () => void;
}

export function BasemapPanel({ onClose }: BasemapPanelProps) {
  const { t } = useTranslation();
  const basemap = useAppStore((s) => s.basemap);
  const setBasemap = useAppStore((s) => s.setBasemap);

  const pick = (key: Basemap) => {
    setBasemap(key);
    onClose();
  };

  return (
    <div
      id="basemapPanel"
      className="show"
      role="radiogroup"
      aria-label={t('tools.basemapPanel')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="sb-title">{t('tools.basemap')}</div>
      {BASEMAP_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={key === basemap}
          className={`bm-opt${key === basemap ? ' active' : ''}`}
          data-bm={key}
          onClick={() => pick(key)}
        >
          <span className="bm-thumb" style={{ background: BASEMAPS[key].thumb }} />
          {BASEMAPS[key].name}
        </button>
      ))}
    </div>
  );
}

export default BasemapPanel;
